import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'

interface TenantBranding {
  logoUrl?: string
  primaryColor?: string
  secondaryColor?: string
  gymName: string
}

interface Tenant {
  tenantId: string
  subdomain: string
  gymName: string
  status: 'ACTIVE' | 'SUSPENDED' | 'PROVISIONING' | 'DELETED'
  branding: TenantBranding
  features?: string[]
}

interface TenantContextType {
  tenant: Tenant | null
  loading: boolean
  error: string | null
  refreshTenant: () => Promise<void>
  refreshFeatures: () => Promise<void>
}

const TenantContext = createContext<TenantContextType | undefined>(undefined)

const DEFAULT_PRIMARY = '#2563eb'

// Extract dominant brand colors from a logo image using Canvas.
// Skips transparent, near-white, near-black, and near-gray pixels.
// Falls back to null on CORS errors or any canvas failure.
async function extractColorsFromLogo(logoUrl: string): Promise<{ primary: string; secondary: string } | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'Anonymous'
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = 60
        canvas.height = 60
        const ctx = canvas.getContext('2d')!
        ctx.drawImage(img, 0, 0, 60, 60)
        const { data } = ctx.getImageData(0, 0, 60, 60)

        const buckets: Record<string, { count: number; r: number; g: number; b: number }> = {}

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3]
          if (a < 128) continue                         // transparent
          const brightness = (r + g + b) / 3
          if (brightness > 225) continue                // near-white
          if (brightness < 25) continue                 // near-black
          if (Math.max(r, g, b) - Math.min(r, g, b) < 45) continue  // near-gray

          // Quantize to 32-step buckets for grouping similar shades
          const br = Math.round(r / 32) * 32
          const bg = Math.round(g / 32) * 32
          const bb = Math.round(b / 32) * 32
          const key = `${br},${bg},${bb}`
          if (!buckets[key]) buckets[key] = { count: 0, r: br, g: bg, b: bb }
          buckets[key].count++
        }

        const sorted = Object.values(buckets).sort((a, b) => b.count - a.count)
        if (sorted.length === 0) { resolve(null); return }

        const p = sorted[0]
        const primaryHex = toHex(p.r, p.g, p.b)

        // Pick a noticeably different second color for secondary, or darken primary
        const s = sorted.find(x => Math.abs(x.r - p.r) + Math.abs(x.g - p.g) + Math.abs(x.b - p.b) > 70)
        const secondaryHex = s ? toHex(s.r, s.g, s.b) : darken(primaryHex, 0.18)

        resolve({ primary: primaryHex, secondary: secondaryHex })
      } catch {
        resolve(null) // CORS taint or canvas error — silent fail
      }
    }
    img.onerror = () => resolve(null)
    img.src = logoUrl
  })
}

function toHex(r: number, g: number, b: number) {
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')
}

function darken(hex: string, amt: number) {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return toHex(
    Math.max(0, Math.round(r * (1 - amt))),
    Math.max(0, Math.round(g * (1 - amt))),
    Math.max(0, Math.round(b * (1 - amt)))
  )
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadTenant()
  }, [])

  async function loadTenant() {
    try {
      setLoading(true)
      setError(null)

      const hostname = window.location.hostname
      const subdomain = extractSubdomain(hostname)

      if (!subdomain) {
        setTenant({
          tenantId: 'default',
          subdomain: 'default',
          gymName: 'ElCapIT Climbing Gym',
          status: 'ACTIVE',
          branding: { gymName: 'ElCapIT Climbing Gym', primaryColor: DEFAULT_PRIMARY, secondaryColor: '#1e40af' }
        })
        setLoading(false)
        return
      }

      const response = await fetch('/api/tenant/info', {
        headers: { 'X-Tenant-Subdomain': subdomain }
      })

      const contentType = response.headers.get('content-type')
      const isJson = contentType?.includes('application/json')

      if (!response.ok || !isJson) {
        setTenant({
          tenantId: subdomain, subdomain,
          gymName: 'ElCapIT Climbing Gym',
          status: 'ACTIVE',
          branding: { gymName: 'ElCapIT Climbing Gym', primaryColor: DEFAULT_PRIMARY, secondaryColor: '#1e40af' }
        })
        setLoading(false)
        return
      }

      const tenantData: Tenant = await response.json()

      // If the gym has a logo but no custom colors stored, extract them from the logo
      const hasCustomColors = tenantData.branding?.primaryColor &&
        tenantData.branding.primaryColor !== DEFAULT_PRIMARY
      if (tenantData.branding?.logoUrl && !hasCustomColors) {
        const extracted = await extractColorsFromLogo(tenantData.branding.logoUrl)
        if (extracted) {
          tenantData.branding.primaryColor = extracted.primary
          tenantData.branding.secondaryColor = extracted.secondary
        }
      }

      setTenant(tenantData)
      applyBranding(tenantData.branding)
    } catch (err: any) {
      console.warn(`[TenantContext] Failed to load tenant info: ${err?.name} - ${err?.message}. Using fallback.`)
      const subdomain = extractSubdomain(window.location.hostname)
      setTenant({
        tenantId: subdomain || 'default', subdomain: subdomain || 'default',
        gymName: 'ElCapIT Climbing Gym',
        status: 'ACTIVE',
        branding: { gymName: 'ElCapIT Climbing Gym', primaryColor: DEFAULT_PRIMARY, secondaryColor: '#1e40af' }
      })
      setError(null)
    } finally {
      setLoading(false)
    }
  }

  function extractSubdomain(hostname: string): string | null {
    if (hostname === 'localhost' || hostname.startsWith('127.0.0.1')) return null
    const parts = hostname.split('.')
    return parts.length >= 3 ? parts[0] : null
  }

  function applyBranding(branding: TenantBranding) {
    if (branding.primaryColor) {
      document.documentElement.style.setProperty('--primary-color', branding.primaryColor)
    }
    if (branding.secondaryColor) {
      document.documentElement.style.setProperty('--secondary-color', branding.secondaryColor)
    }
    document.title = branding.gymName || 'ElCapIT Climbing Gym'
  }

  async function refreshTenant() {
    await loadTenant()
  }

  async function refreshFeatures() {
    try {
      const session = await fetchAuthSession()
      const token = session.tokens?.idToken?.toString()
      if (!token) return

      const response = await fetch('/api/tenant/config', {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!response.ok) return

      const { features } = await response.json()
      setTenant(prev => prev ? { ...prev, features } : prev)
    } catch {
      // silently ignore — nav will show all items as fallback
    }
  }

  return (
    <TenantContext.Provider value={{ tenant, loading, error, refreshTenant, refreshFeatures }}>
      {children}
    </TenantContext.Provider>
  )
}

export function useTenant() {
  const context = useContext(TenantContext)
  if (context === undefined) throw new Error('useTenant must be used within a TenantProvider')
  return context
}
